import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./GeneratePaymentBatch.css";
import { formatDbTimestamp } from "../../utils";

export interface StoreBatchFilters {
  search?: string;
  balance?: number;
  startDate?: string;
  endDate?: string;
  timezone?: string;
}

interface GeneratePaymentBatchProps {
  selectedStoreIds: number[];
  isAllSelected?: boolean;
  excludedStoreIds?: number[];
  filters?: StoreBatchFilters;
  hasActiveFilters?: boolean;
  filterStartDate?: Date | null;
  filterEndDate?: Date | null;
  onSuccess?: (data: any) => void;
}

type StatusState = {
  type: "success" | "error" | "info";
  message: string;
} | null;

interface ValidBatchStore {
  store_id: number;
  store_name?: string;
  start_date?: string | Date;
  start_date_type?: string;
  end_date?: string | Date;
  amount_release?: number;
}

interface SkippedBatchStore {
  store_id: number;
  store_name?: string;
  start_date?: string | Date;
  start_date_type?: string;
  reason?: string;
  reason_type?: string;
}

interface BatchResultData {
  preview?: boolean;
  total_stores?: number;
  total_amount?: number;
  total_included?: number;
  total_skipped?: number;
  included_store_ids?: number[];
  skipped_store_ids?: number[];
  stores?: ValidBatchStore[];
  skipped_stores?: SkippedBatchStore[];
  file_name?: string;
  file_url?: string;
  batch?: {
    id: number;
    file_name?: string;
    total_stores?: number;
    total_amount?: number;
  };
}

const formatDisplayDate = (d: string | Date | null | undefined): string => {
  if (!d) return "—";
  return formatDbTimestamp(d, { month: "short", day: "numeric", year: "numeric" });
};

const getCleanStoreName = (name: string | undefined, id: number): string => {
  if (!name) return `Store #${id}`;
  const regex = new RegExp(`^#?${id}\\s*[-:]?\\s*`, "i");
  return name.replace(regex, "").trim() || name;
};

const formatMoney = (n: number | string | null | undefined): string =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// True when the store's skip issue is fixable by editing its payee details.
// Backend sends exactly 'Missing payee name or mailing address' for these;
// matched loosely (payee / mailing) so minor wording changes still resolve.
const isPayeeInfoIssue = (reason: string | undefined): boolean =>
  Boolean(
    reason &&
      /\bmissing\b/i.test(reason) &&
      /\b(payee|mailing)\b/i.test(reason)
  );

const renderIssueReason = (reason: string | undefined) => {  if (!reason) return "Ineligible for batch";
  const batchMatch = reason.match(/(.*?\bBatch\s*#\d+\b)(.*)/i);
  if (batchMatch) {
    const beforeOrBatch = batchMatch[1];
    const after = batchMatch[2];
    const prefix = beforeOrBatch.replace(/\bBatch\s*#\d+\b/i, "");
    const batchPart = beforeOrBatch.slice(prefix.length);
    return (
      <>
        {prefix}
        <span className="generate-batch-issue-link">{batchPart}</span>
        {after}
      </>
    );
  }
  return reason;
};

const GeneratePaymentBatch: React.FC<GeneratePaymentBatchProps> = ({
  selectedStoreIds,
  isAllSelected = false,
  excludedStoreIds = [],
  filters,
  hasActiveFilters = false,
  filterStartDate,
  filterEndDate,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [endDate, setEndDate] = useState<Date | null>(filterEndDate || new Date());
  const [status, setStatus] = useState<StatusState>(null);
  const [batchData, setBatchData] = useState<BatchResultData | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store latest props in ref to prevent unnecessary useEffect re-runs
  const propsRef = useRef({
    selectedStoreIds,
    isAllSelected,
    excludedStoreIds,
    filters,
    hasActiveFilters,
  });
  propsRef.current = {
    selectedStoreIds,
    isAllSelected,
    excludedStoreIds,
    filters,
    hasActiveFilters,
  };

  const fetchPreviewData = useCallback(async (targetDate: Date) => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setStatus(null);
    setIsPreviewLoading(true);

    try {
      const pad = (n: number) => String(n).padStart(2, "0");
      const formattedDate = `${targetDate.getFullYear()}-${pad(
        targetDate.getMonth() + 1
      )}-${pad(targetDate.getDate())}`;

      const {
        isAllSelected: allSel,
        excludedStoreIds: exclIds,
        selectedStoreIds: selIds,
        hasActiveFilters: hasFilters,
        filters: currFilters,
      } = propsRef.current;

      const payload = allSel
        ? {
            store_ids: [],
            all_stores: true,
            excluded_store_ids: exclIds,
            end_date: formattedDate,
            preview: true,
            // Do NOT send startDate: the page's date-range start filter produces
            // incorrect batch results. Each store's start date is computed
            // server-side (last issued date or store creation date).
            ...(hasFilters && currFilters
              ? { filters: { ...currFilters, startDate: undefined, endDate: undefined } }
              : {}),
          }
        : {
            store_ids: selIds,
            end_date: formattedDate,
            preview: true,
            // Do NOT send startDate: the page's date-range start filter produces
            // incorrect batch results. Each store's start date is computed
            // server-side (last issued date or store creation date).
            ...(hasFilters && currFilters
              ? { filters: { ...currFilters, startDate: undefined, endDate: undefined } }
              : {}),
          };

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/payments/batches/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        }
      );

      const result = await response.json();

      if (response.ok && result?.success && result?.data) {
        setBatchData(result.data);
      } else {
        const msg =
          result?.message?.replace(/^Error in \w+:\s*/i, "") ||
          "Failed to preview payment batch.";
        setStatus({ type: "error", message: msg });
        const errorSkipped =
          result?.data?.skipped_stores ||
          result?.data?.details?.skipped_stores ||
          [];
        if (errorSkipped.length > 0) {
          setBatchData({
            skipped_stores: errorSkipped,
            total_skipped: errorSkipped.length,
            total_included: 0,
            stores: [],
          });
        }
      }
    } catch (err: any) {
      if (err.name === "AbortError") return;
      setStatus({
        type: "error",
        message: err?.message || "Failed to preview batch.",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const initialDate = filterEndDate || new Date();
      fetchPreviewData(initialDate);
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open, filterEndDate, fetchPreviewData]);

  const handleOpen = () => {
    if (!isAllSelected && selectedStoreIds.length === 0) return;
    const initialDate = filterEndDate || new Date();
    setEndDate(initialDate);
    setStatus(null);
    setBatchData(null);
    setIsPreviewLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    setOpen(false);
  };

  const handleDateChange = (date: Date | null) => {
    setEndDate(date);
    if (date) {
      fetchPreviewData(date);
    }
  };

  const downloadGeneratedFile = async (
    fileUrl: string | undefined,
    batchId?: number,
    fileNameInput?: string
  ) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (batchId) params.append("batch_id", String(batchId));
      if (fileUrl) params.append("file_url", fileUrl);
      if (fileNameInput) params.append("file_name", fileNameInput);

      const url = `${import.meta.env.VITE_API_URL}/payments/batches/download?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download payment batch spreadsheet");
      }

      const blob = await response.blob();
      const downloadBlobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = downloadBlobUrl;
      const fileName =
        fileNameInput ||
        (fileUrl && fileUrl.split("/").pop()) ||
        (batchId ? `Payment_Release_Batch_${batchId}.xlsx` : `Payment_Release_Batch.xlsx`);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        if (a.parentNode) {
          a.parentNode.removeChild(a);
        }
        window.URL.revokeObjectURL(downloadBlobUrl);
      }, 1000);
    } catch (err) {
      console.error("Batch Excel download error:", err);
    }
  };

  const handleSubmit = async () => {
    if (!endDate) {
      setStatus({ type: "error", message: "Please select an end date." });
      return;
    }
    setStatus(null);
    setIsSubmitting(true);

    try {
      const pad = (n: number) => String(n).padStart(2, "0");
      const formattedDate = `${endDate.getFullYear()}-${pad(
        endDate.getMonth() + 1
      )}-${pad(endDate.getDate())}`;

      const {
        isAllSelected: allSel,
        excludedStoreIds: exclIds,
        selectedStoreIds: selIds,
        hasActiveFilters: hasFilters,
        filters: currFilters,
      } = propsRef.current;

      const payload = allSel
        ? {
            store_ids: [],
            all_stores: true,
            excluded_store_ids: exclIds,
            end_date: formattedDate,
            preview: false,
            // Do NOT send startDate: the page's date-range start filter produces
            // incorrect batch results. Each store's start date is computed
            // server-side (last issued date or store creation date).
            ...(hasFilters && currFilters
              ? { filters: { ...currFilters, startDate: undefined } }
              : {}),
          }
        : {
            store_ids: selIds,
            end_date: formattedDate,
            preview: false,
            // Do NOT send startDate: the page's date-range start filter produces
            // incorrect batch results. Each store's start date is computed
            // server-side (last issued date or store creation date).
            ...(hasFilters && currFilters
              ? { filters: { ...currFilters, startDate: undefined } }
              : {}),
          };

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/payments/batches/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok && result?.success) {
        const msg = result?.message || "Payment batch generated successfully.";
        setStatus({ type: "success", message: msg });
        setBatchData(result?.data || null);

        const fileUrl = result?.data?.file_url;
        const batchId = result?.data?.batch?.id;
        const fileName = result?.data?.file_name;

        // Download Excel file automatically then and there
        await downloadGeneratedFile(fileUrl, batchId, fileName);

        if (onSuccess) {
          onSuccess(result);
        }
        // Keep message visible until user clears it, closes, or refreshes
      } else {
        const msg =
          result?.message?.replace(/^Error in \w+:\s*/i, "") ||
          "Failed to generate payment batch.";
        setStatus({ type: "error", message: msg });
      }
    } catch (err: any) {
      setStatus({
        type: "error",
        message: err?.message || "Failed to generate payment batch.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const issuesStores: SkippedBatchStore[] = useMemo(() => {
    return (batchData?.skipped_stores ?? []);
  }, [batchData]);

  const includedStores: ValidBatchStore[] = useMemo(() => {
    if (!batchData?.stores) return [];
    return batchData.stores;
  }, [batchData]);

  const totalPayout = useMemo(() => {
    if (batchData?.total_amount != null) return Number(batchData.total_amount);
    return includedStores.reduce(
      (sum, s) => sum + (Number(s.amount_release) || 0),
      0
    );
  }, [batchData, includedStores]);

  const canGenerate =
    Boolean(endDate) &&
    !isSubmitting &&
    !isPreviewLoading &&
    includedStores.length > 0 &&
    status?.type !== "success";

  return (
    <>
      <button
        type="button"
        className="collection-button add-button-styling stores-generate-batch-btn"
        onClick={handleOpen}
        disabled={!isAllSelected && selectedStoreIds.length === 0}
      >
        {isAllSelected
          ? excludedStoreIds.length > 0
            ? "Generate Payment Batch (Selected)"
            : "Generate Payment Batch"
          : selectedStoreIds.length > 0
          ? `Generate Payment Batch (${selectedStoreIds.length})`
          : "Generate Payment Batch"}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        disableScrollLock
        classes={{ paper: "generate-batch-dialog-paper" }}
      >
        <button
          type="button"
          className="generate-batch-dialog-close-btn"
          onClick={handleClose}
          disabled={isSubmitting || isPreviewLoading}
          aria-label="Close dialog"
        >
          <CloseIcon className="generate-batch-close-icon" />
        </button>

        <DialogContent className="generate-batch-dialog-content">
          <h2 className="generate-batch-title">Generate Payout Batch</h2>

          {/* ---------- End Date row + hint ---------- */}
          <div className="generate-batch-dates-row">
            <label
              className="generate-batch-dates-label"
              htmlFor="generate-batch-end-date"
            >
              End Date
            </label>
            <div className="generate-batch-picker-wrapper">
              <DatePicker
                id="generate-batch-end-date"
                selected={endDate}
                onChange={handleDateChange}
                disabled={isSubmitting || isPreviewLoading || status?.type === "success"}
                minDate={filterStartDate || undefined}
                maxDate={new Date()}
                dateFormat="MMM d, yyyy"
                className="generate-batch-date-input"
                popperClassName="generate-batch-datepicker-popper"
                popperPlacement="bottom-start"
                portalId="generate-batch-datepicker-portal"
                popperProps={{ strategy: "fixed" }}
                aria-label="End date for the payout batch"
              />
              <CalendarTodayOutlinedIcon className="generate-batch-calendar-icon" />
            </div>
            <span className="generate-batch-dates-hint">
              Start date is each store's last issued date, or its creation date.
            </span>
          </div>

          {/* ---------- Status Message Banner if error or success ---------- */}
          {status && (
            <div className="generate-batch-status-container">
              <p className={`generate-batch-status-text generate-batch-status-text--${status.type}`}>
                {status.message}
              </p>
              <button
                type="button"
                className="generate-batch-status-dismiss"
                onClick={() => setStatus(null)}
                aria-label="Dismiss message"
              >
                ✕
              </button>
            </div>
          )}

          {/* ============================================================
              SINGLE PAGE: combined ready stores + not-included stores
              ============================================================ */}
          <div className="generate-batch-body-container">
            {isPreviewLoading && !batchData ? (
              <div className="generate-batch-table-loading">
                <div className="generate-batch-spinner" />
                <span>Checking store eligibility...</span>
              </div>
            ) : !batchData ? (
              <div className="generate-batch-empty-msg">
                No stores are eligible for this payout batch.
              </div>
            ) : (
              <div className="generate-batch-blocks">
                {isPreviewLoading && (
                  <div className="generate-batch-refresh-overlay" aria-hidden="true">
                    <div className="generate-batch-spinner" />
                    <span>Updating stores preview...</span>
                  </div>
                )}

                {includedStores.length === 0 && issuesStores.length === 0 ? (
                  <div className="generate-batch-empty-msg">
                    No stores are eligible for this payout batch.
                  </div>
                ) : (
                  <>
                    {/* ---------- Block 1: stores ready for payout ---------- */}
                    {includedStores.length > 0 && (
                      <section
                        className="generate-batch-block generate-batch-block--ok"
                        aria-label="Stores ready for payout"
                      >
                        <div className="generate-batch-block-head">
                          <CheckCircleOutlineIcon className="generate-batch-block-icon generate-batch-block-icon--ok" />
                          <div className="generate-batch-block-head-text">
                            <h3 className="generate-batch-block-title generate-batch-block-title--ok">
                              Ready for payout
                              <span className="generate-batch-count-badge">
                                {includedStores.length}{" "}
                                {includedStores.length === 1 ? "store" : "stores"}
                              </span>
                            </h3>
                            <p className="generate-batch-block-sub">
                              Unpaid balances up to {formatDisplayDate(endDate)}
                            </p>
                          </div>
                        </div>

                        <div className="generate-batch-scroll generate-batch-scroll--ok">
                          <table className="generate-batch-table generate-batch-table--ready">
                            <thead>
                              <tr>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--store"
                                >
                                  Store
                                </th>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--start-date"
                                >
                                  Start Date
                                </th>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--balance"
                                >
                                  Unpaid Balance
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {includedStores.map((store) => (
                                <tr key={store.store_id} className="generate-batch-tr">
                                  <td
                                    className="generate-batch-td generate-batch-td--store"
                                    title={`#${store.store_id} ${getCleanStoreName(
                                      store.store_name,
                                      store.store_id
                                    )}`}
                                  >
                                    <span className="generate-batch-store-id">
                                      #{store.store_id}
                                    </span>{" "}
                                    <span className="generate-batch-store-name">
                                      {getCleanStoreName(store.store_name, store.store_id)}
                                    </span>
                                  </td>
                                  <td className="generate-batch-td generate-batch-td--start-date">
                                    <div className="generate-batch-date-cell">
                                      <span className="generate-batch-cell-date">
                                        {formatDisplayDate(store.start_date)}
                                      </span>
                                      <span className="generate-batch-cell-subtext">
                                        {store.start_date_type || "Last issued"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="generate-batch-td generate-batch-td--balance">
                                    <span className="generate-batch-balance-text">
                                      ${formatMoney(store.amount_release)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="generate-batch-block-foot">
                          <div className="generate-batch-block-total">
                            Total ({includedStores.length}{" "}
                            {includedStores.length === 1 ? "store" : "stores"})
                            <span className="generate-batch-block-total-amount">
                              ${formatMoney(totalPayout)}
                            </span>
                          </div>

                          {status?.type !== "success" ? (
                            <button
                              type="button"
                              className="generate-batch-btn generate-batch-btn--submit generate-batch-btn--excel"
                              onClick={handleSubmit}
                              disabled={!canGenerate}
                            >
                              <DownloadIcon className="generate-batch-download-icon" />
                              <span>{isSubmitting ? "Generating..." : "Generate Excel"}</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="generate-batch-btn generate-batch-btn--submit generate-batch-btn--excel"
                              onClick={() => {
                                const fileUrl = batchData?.file_url;
                                const batchId = batchData?.batch?.id;
                                const fileName = batchData?.file_name;
                                downloadGeneratedFile(fileUrl, batchId, fileName);
                              }}
                            >
                              <DownloadIcon className="generate-batch-download-icon" />
                              <span>Download Excel Again</span>
                            </button>
                          )}
                        </div>
                      </section>
                    )}

                    {/* ---------- Block 2: stores not included in this batch ---------- */}
                    {issuesStores.length > 0 && (
                      <section
                        className="generate-batch-block generate-batch-block--bad"
                        aria-label="Stores not included in this batch"
                      >
                        <div className="generate-batch-block-head">
                          <ErrorOutlineIcon className="generate-batch-block-icon generate-batch-block-icon--bad" />
                          <div className="generate-batch-block-head-text">
                            <h3 className="generate-batch-block-title generate-batch-block-title--bad">
                              Not included in this batch
                              <span className="generate-batch-count-badge">
                                {issuesStores.length}{" "}
                                {issuesStores.length === 1 ? "store" : "stores"}
                              </span>
                            </h3>
                            <p className="generate-batch-block-sub">
                              Fix the issues below, then generate again to include these stores.
                            </p>
                          </div>
                        </div>

                        <div className="generate-batch-scroll generate-batch-scroll--bad">
                          <table className="generate-batch-table generate-batch-table--issues">
                            <thead>
                              <tr>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--store"
                                >
                                  Store
                                </th>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--start-date"
                                >
                                  Start Date
                                </th>
                                <th
                                  scope="col"
                                  className="generate-batch-th generate-batch-th--issue"
                                >
                                  Issue
                                </th>
                                <th
                                  scope="col"
                                  aria-label="Edit action"
                                  className="generate-batch-th generate-batch-th--action"
                                ></th>
                              </tr>
                            </thead>
                            <tbody>
                              {issuesStores.map((store) => (
                                <tr key={store.store_id} className="generate-batch-tr">
                                  <td
                                    className="generate-batch-td generate-batch-td--store"
                                    title={`#${store.store_id} ${getCleanStoreName(
                                      store.store_name,
                                      store.store_id
                                    )}`}
                                  >
                                    <span className="generate-batch-store-id">
                                      #{store.store_id}
                                    </span>{" "}
                                    <span className="generate-batch-store-name">
                                      {getCleanStoreName(store.store_name, store.store_id)}
                                    </span>
                                  </td>
                                  <td className="generate-batch-td generate-batch-td--start-date">
                                    <div className="generate-batch-date-cell">
                                      <span className="generate-batch-cell-date">
                                        {formatDisplayDate(store.start_date)}
                                      </span>
                                      <span className="generate-batch-cell-subtext">
                                        {store.start_date_type || "Last issued"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="generate-batch-td generate-batch-td--issue">
                                    <span className="generate-batch-issue-text">
                                      {renderIssueReason(store.reason)}
                                    </span>
                                  </td>
                                  <td className="generate-batch-td generate-batch-td--action">
                                    {isPayeeInfoIssue(store.reason) ? (
                                      <Link
                                        to={`/payouts/${store.store_id}`}
                                        className="generate-batch-edit-link"
                                        onClick={(e) => e.stopPropagation()}
                                        target="_blank"
                                      >
                                        Edit
                                      </Link>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ---------- Bottom actions ---------- */}
          <div className="generate-batch-actions generate-batch-actions--end">
            <button
              type="button"
              className="generate-batch-btn generate-batch-btn--cancel"
              onClick={handleClose}
              disabled={isPreviewLoading}
            >
              {status?.type === "success" ? "Close" : "Cancel"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GeneratePaymentBatch;
